import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

interface CtaLink {
  label: string;
  to: string;
}

interface MeshHeroProps {
  badge?: string;
  heading: string;
  subheading: string;
  primaryCta?: CtaLink;
  secondaryCta?: CtaLink;
  trialNote?: string;
}

/**
 * Shared hero shell for the rumio.world SaaS marketing pages
 * (/, /features, /case-studies). Warm cream→white gradient ground, two
 * decorative blurred blobs, a badge pill, oversized display heading, and an
 * optional dual CTA. Mirrors the geometry of the original SaasHome hero so the
 * pages read as siblings. All colors are semantic theme tokens (sage/cream/
 * terra) so per-tenant palettes + dark mode resolve correctly.
 */
export default function MeshHero({
  badge,
  heading,
  subheading,
  primaryCta,
  secondaryCta,
  trialNote,
}: MeshHeroProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-cream to-surface">
      {/* Decorative blurred blobs — purely visual, never announced. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-sage-200/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-terra-light/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 sm:py-32 lg:px-8">
        {badge && (
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-sage-100 px-4 py-1.5 text-xs font-medium text-sage-800">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {badge}
          </div>
        )}

        <h1 className="font-display text-4xl font-bold text-sage-900 sm:text-5xl lg:text-6xl">
          {heading}
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-sage-700 sm:text-xl">{subheading}</p>

        {(primaryCta || secondaryCta) && (
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
            {primaryCta && (
              <Link
                to={primaryCta.to}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-cta px-8 py-4 text-base font-semibold text-cta-fg transition-colors hover:bg-cta-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-900 focus-visible:ring-offset-2"
              >
                {primaryCta.label}
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.to}
                className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-sage-300 bg-surface px-8 py-4 text-base font-semibold text-sage-800 transition-colors hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
              >
                {secondaryCta.label}
              </Link>
            )}
          </div>
        )}

        {trialNote && <p className="mt-4 text-sm text-sage-500">{trialNote}</p>}
      </div>
    </section>
  );
}
